
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Typed from 'typed.js';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import DNSRepositoryInterface from '@/services/DNS/Interfaces';
import { CertificatesRepositoryInterface } from '@/services/SSL/interfaces';
import { MailboxRepositoryInterface } from '@/services/Mailbox/interfaces';
import { Roles } from '@/services/Roles';
import { hasScope } from '@/views/middleware/Route/Auth/UserHasScopes';
import { buildPageCatalog } from './pageCatalog';
import {
  browsePageChipLabel,
  buildConsoleBridgeItems,
  filterPages,
  filterRecent,
  filterServers,
  flattenSections,
} from './search';
import {
  clearRecentJumps,
  loadRecentJumps,
  pushRecentJump,
  recentToPaletteItems,
} from './recent';
import { loadPalettePrefs, PaletteOperatorPrefs, savePalettePrefs } from './prefs';
import { modLabel } from './hotkeys';
import {
  loadSiteContext,
  PaletteSiteContext,
  saveSiteContext,
  siteStatusTone,
} from './siteContext';
import {
  buildSiteActionItems,
  loadSiteDnsItems,
  loadSiteMailItems,
  loadSiteSslItems,
  shouldAutoLoadResource,
  SiteResourceDeps,
} from './siteResources';
import { PaletteItem, PaletteSection } from './types';

const NAV_ACCEL_MAX = 10;
const NAV_SPARK_AT = 3;
const NAV_SPARK_MAX = 64;
const REVS_SEGMENTS = 14;
/** Sonic boom / hyperdrive cue in public/sounds/engine.mp3. */
const ENGINE_HYPERDRIVE_AT = 14;
/** Loop after the boom has fully finished (not 14–15). */
const ENGINE_LOOP_AT = 16;
const ENGINE_SRC = '/sounds/engine.mp3';
/** Fire border starts fading in once speed passes this. */
const FIRE_START_KMH = 100;
/** Full fire intensity by redline / hyperdrive entry. */
const FIRE_FULL_KMH = 320;
/** Idle Typed.js tips in the empty search field (does not write to query). */
const IDLE_TYPED_STRINGS = [
  'Jump to page, website, or server…',
  'Try a domain — example.com',
  'Or jump by website id…',
  'All Servers',
  'Pin a site with Tab',
  'Then type dns, ssl, or mail',
  'Backups · DNS · SSL · Mailboxes',
  'Open the website console…',
  'Hey — type something. I\'ll find it.',
];

const CHIBI_PET_REACTIONS = [
  'Meowww',
  'Purrrr',
  'Mrrp',
  'Nyaa~',
  'Purr purr',
  'Meow!',
  'Mrrrow',
  'Prrrt',
  '*kneads*',
  '♥ soft boop',
  'Hehe mew',
  'Chirp!',
  'Brrr-up',
  'Headbutt ♥',
  'Scritches pls',
  'Mmmrrph',
];

const CHIBI_ANGRY_REACTIONS = [
  'HISS!!',
  'NO MOUSE',
  'GRRR',
  'How dare you',
  ' >:3 ',
  'Mice are CRIMES',
  'DELETE THAT',
  'I saw nothing',
  'TRAITOR',
  'Pffffft!',
  'Angry meow',
  'Not today, rodent',
];

const CHIBI_ANGRY_WORDS = [
  'mouse',
  'vmax',
  'fenix',
  'downtime',
  'kor',
  'refactor',
];

const CHIBI_REACTION_TONES = ['pink', 'peach', 'lilac', 'mint'];
const CHIBI_ANGRY_TONES = ['rage', 'ember'];

/** Delay before ear tips peek after palette opens. */
const CHIBI_APPEAR_MS = 1000;
/** Must match `.global-palette__chibi` transform transition duration. */
const CHIBI_RAISE_MS = 550;

/** Delay after hitting 100 km/h before ship AI starts talking. */
const AI_CHAT_DELAY_MS = 2000;
/** Hold an arrow this long before engine / warp drive engages. */
const DRIVE_HOLD_MS = 500;
const AI_TYPE_MS = 28;
const AI_GAP_MS = 700;
const AI_MAX_BUBBLES = 5;

const SHIP_AI_LINES = [
  'Hull telemetry online. Host fabric within nominal range.',
  'Warm origins detected — your domains are already breathing.',
  'Edge relays locked. Latency corridors clearing ahead.',
  'SSL lattice intact. Certificates riding the warp wake.',
  'DNS pulses synchronized across the cluster mesh.',
  'WordPress cores stable. Plugin drift: zero.',
  'Staging mirror armed on vector-7 if you need a fork.',
  'Mailbox conduits open. Spam shields holding at 99.7%.',
  'Origin servers reporting green across the jump lane.',
  'Hyperdrive hosting corridor open. Jump when ready, operator.',
];

const SHIP_AI_ENTER = 'Hyperdrive engaged. Jump corridor locked — host fabric at warp.';
const SHIP_AI_EXIT = 'Hyperdrive disengaged. Back to sublight. Corridor sealed, operator.';
const SHIP_AI_EXIT_CRUISE = 'Throttle cut. Leaving the lane — host fabric back to idle, operator.';

interface NavSpark {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  size: number;
  hot: boolean;
}

interface HyperStar {
  angle: number;
  dist: number;
  speed: number;
  len: number;
  bright: number;
}

interface AiBubble {
  id: number;
  full: string;
  shown: string;
  done: boolean;
  fresh: boolean;
}

const HYPER_STAR_COUNT = 96;

function isSiteSelectable(item: PaletteItem): boolean {
  if (item.kind === 'website') {
    return true;
  }
  if (item.kind === 'recent' && item.meta && String(item.meta.sourceKind) === 'website') {
    return Number(item.meta.id) > 0;
  }
  return false;
}

@Component({
  inject: [
    'websiteRepository',
    'serverRepository',
    'dnsRepository',
    'certificateRepository',
    'mailboxRepository',
  ],
})
export default class GlobalJumpPalette extends Vue {
  @Prop({ type: Boolean, default: false })
  public value!: boolean;

  private websiteRepository!: WebsiteRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;
  private dnsRepository!: DNSRepositoryInterface;
  private certificateRepository!: CertificatesRepositoryInterface;
  private mailboxRepository!: MailboxRepositoryInterface;

  public query = '';
  public activeIndex = 0;
  public loadingWebsites = false;
  public loadingResources = false;
  public websiteError = '';
  public serverError = '';
  public resourceError = '';
  public websiteItems: PaletteItem[] = [];
  public serverItems: PaletteItem[] = [];
  public pageCatalog: PaletteItem[] = [];
  public recentItems: PaletteItem[] = [];
  public siteActionItems: PaletteItem[] = [];
  public resourceItems: PaletteItem[] = [];
  public selectedSite: PaletteSiteContext | null = null;
  public showPrefs = false;
  public prefs: PaletteOperatorPrefs = loadPalettePrefs();
  public activeResource: 'dns' | 'ssl' | 'mail' | null = null;

  private serversCache: ServerInterface[] | null = null;
  private serversLoading: Promise<void> | null = null;
  private hostnameMap: { [id: string]: string } = {};
  private searchTimer: number | null = null;
  private searchSeq = 0;
  private resourceSeq = 0;
  private idleTyped: Typed | null = null;
  private heldArrow: string | null = null;
  private driveDirection: 'up' | 'down' | 'left' | 'right' | null = null;
  private holdStartedAt = 0;
  private driveEngaged = false;
  public navAccel = 0;
  public tripKm = 0;
  public fireIntensity = 0;
  public hyperdrive = false;
  public hyperdriveKmh = 0;
  public displayKmh = 0;
  public coasting = false;
  public aiBubbles: AiBubble[] = [];
  public sparks: NavSpark[] = [];
  public chibiVisible = false;
  public chibiEars = false;
  public chibiRaised = false;
  public chibiFront = false;
  public chibiPetting = false;
  public chibiReaction = '';
  public chibiReactionTone = 'pink';
  public chibiReactionFx = 'float';
  public chibiPetSeq = 0;
  private chibiAppearTimer: number | null = null;
  private chibiFrontTimer: number | null = null;
  private chibiPetTimer: number | null = null;
  private chibiReactionTimer: number | null = null;
  private sparkSeq = 0;
  private sparkRaf: number | null = null;
  private lastSparkTs = 0;
  private engineAudio: HTMLAudioElement | null = null;
  private driveRaf: number | null = null;
  private lastDriveTs = 0;
  private hyperStars: HyperStar[] = [];
  private hyperRaf: number | null = null;
  private aiAboveMs = 0;
  private aiStarted = false;
  private aiExiting = false;
  private aiLineIdx = 0;
  private aiSeq = 0;
  private aiTypeTimer: number | null = null;
  private aiGapTimer: number | null = null;

  public get mod(): string {
    return modLabel();
  }

  public get hasWebsiteScope(): boolean {
    return hasScope(Roles.WEBSITE_WEBSITE_READ);
  }

  public get hasServerScope(): boolean {
    return hasScope(Roles.SERVER_SERVER_READ);
  }

  public get isBrowseMode(): boolean {
    return !this.query.trim() && !this.selectedSite;
  }

  public get showIdleTyped(): boolean {
    return this.value && !this.query.trim() && !this.selectedSite;
  }

  public get chibiAngry(): boolean {
    const q = this.query.toLowerCase();
    return CHIBI_ANGRY_WORDS.some((word) => q.indexOf(word) !== -1);
  }

  public get inputPlaceholder(): string {
    if (this.selectedSite) {
      return 'Filter site actions, or type dns / ssl / mail…';
    }
    return 'Jump to page, website, or server…';
  }

  public get emptyHint(): string {
    if (this.selectedSite) {
      return 'No matches. Try dns, ssl, mail, open, or clear the site chip.';
    }
    return 'No matches. Try a page name, domain, or server hostname.';
  }

  public get siteTone(): string {
    return this.selectedSite ? siteStatusTone(this.selectedSite.status) : 'muted';
  }

  public get selectedSiteSummary(): string {
    if (!this.selectedSite) {
      return '';
    }
    const host = this.selectedSite.hostname || this.selectedSite.originServer || '—';
    return `#${this.selectedSite.id} · ${this.selectedSite.status} · ${host}`;
  }

  public get activeItem(): PaletteItem | null {
    const items = this.flatItems;
    if (!items.length) {
      return null;
    }
    return items[this.activeIndex] || null;
  }

  public get showUseSiteTip(): boolean {
    return this.prefs.showSiteUseTip
      && !this.sparksActive
      && !this.selectedSite
      && !this.showPrefs
      && !!this.activeItem
      && isSiteSelectable(this.activeItem);
  }

  public get showClearSiteTip(): boolean {
    return this.prefs.showSiteClearTip
      && !this.sparksActive
      && !!this.selectedSite
      && !this.showPrefs;
  }

  public get sparksActive(): boolean {
    return this.sparks.length > 0
      || this.navAccel >= NAV_SPARK_AT
      || (this.coasting && this.displayKmh >= FIRE_START_KMH);
  }

  public get driveSpooling(): boolean {
    return (this.driveEngaged && !!this.heldArrow && this.navAccel > 0.2)
      || (this.coasting && this.displayKmh > 8);
  }

  public get showFire(): boolean {
    return this.fireIntensity > 0.02;
  }

  public get firePanelStyle(): { [key: string]: string } | undefined {
    if (!this.showFire) {
      return undefined;
    }
    const i = this.fireIntensity;
    return {
      '--fire-i': String(i),
      borderColor: `rgba(255, ${Math.round(106 + (40 * (1 - i)))}, 0, ${0.25 + (0.75 * i)})`,
      boxShadow: [
        `0 0 ${6 + (10 * i)}px ${1 + (2 * i)}px rgba(255, 90, 0, ${0.2 + (0.65 * i)})`,
        `0 0 ${12 + (24 * i)}px ${2 + (6 * i)}px rgba(255, 60, 0, ${0.12 + (0.43 * i)})`,
        `0 0 ${20 + (40 * i)}px ${4 + (8 * i)}px rgba(255, 30, 0, ${0.08 + (0.27 * i)})`,
        `inset 0 0 ${8 + (14 * i)}px rgba(255, 100, 0, ${0.06 + (0.16 * i)})`,
        '0 18px 48px rgba(0, 0, 0, 0.45)',
      ].join(', '),
    };
  }

  public get fireRingStyle(): { [key: string]: string } {
    return {
      opacity: String(this.fireIntensity),
    };
  }

  public get panelShakeClass(): string {
    if (this.coasting) {
      if (this.displayKmh >= FIRE_FULL_KMH) {
        return 'global-palette__panel--shake-med';
      }
      if (this.displayKmh >= FIRE_START_KMH) {
        return 'global-palette__panel--shake';
      }
      return '';
    }
    if (this.hyperdrive || this.navAccel >= NAV_ACCEL_MAX) {
      return 'global-palette__panel--shake-hard';
    }
    if (this.navAccel < NAV_SPARK_AT) {
      return '';
    }
    if (this.navAccel >= Math.ceil(NAV_ACCEL_MAX * 0.65)) {
      return 'global-palette__panel--shake-med';
    }
    return 'global-palette__panel--shake';
  }

  public get revsRatio(): number {
    if (this.coasting) {
      return Math.min(1, this.displayKmh / FIRE_FULL_KMH);
    }
    return Math.min(1, this.navAccel / NAV_ACCEL_MAX);
  }

  public get revsKmh(): string {
    return String(Math.max(0, Math.round(this.currentDriveKmh())));
  }

  public get revsDistance(): string {
    if (this.tripKm < 10) {
      return this.tripKm.toFixed(1);
    }
    if (this.tripKm < 100) {
      return this.tripKm.toFixed(1);
    }
    return String(Math.round(this.tripKm));
  }

  public get revsSegments(): Array<{ on: boolean; color: string; glow: string }> {
    const lit = Math.round(this.revsRatio * REVS_SEGMENTS);
    const segs: Array<{ on: boolean; color: string; glow: string }> = [];
    for (let i = 0; i < REVS_SEGMENTS; i += 1) {
      const t = REVS_SEGMENTS <= 1 ? 0 : i / (REVS_SEGMENTS - 1);
      const color = this.revsColorAt(t);
      segs.push({
        on: i < lit,
        color,
        glow: `0 0 6px ${color}, 0 0 12px ${color}`,
      });
    }
    return segs;
  }

  public get revsToneClass(): string {
    const r = this.revsRatio;
    if (this.hyperdrive || r >= 0.95) {
      return 'global-palette__revs--redline';
    }
    if (r >= 0.7) {
      return 'global-palette__revs--hot';
    }
    if (r >= 0.4) {
      return 'global-palette__revs--warm';
    }
    return 'global-palette__revs--live';
  }

  public revsColorAt(t: number): string {
    if (t < 0.32) {
      return '#3dd68c';
    }
    if (t < 0.52) {
      return '#e6c84a';
    }
    if (t < 0.72) {
      return '#ff9f43';
    }
    return '#ff4d4f';
  }

  public sparkStyle(spark: NavSpark): { [key: string]: string } {
    return {
      left: `${spark.x}px`,
      top: `${spark.y}px`,
      width: `${spark.size}px`,
      height: `${spark.size}px`,
      opacity: String(Math.max(0, Math.min(1, spark.life))),
    };
  }

  public get footerHints(): Array<{ parts: string[]; label: string }> {
    const hints: Array<{ parts: string[]; label: string }> = [
      { parts: ['↑', '↓', '←', '→'], label: 'navigate' },
    ];
    const item = this.activeItem;
    if (!item) {
      if (this.selectedSite) {
        hints.push({ parts: ['Tab'], label: 'clear site' });
      }
      hints.push({ parts: [this.mod, '/'], label: 'shortcuts' });
      return hints;
    }

    if (isSiteSelectable(item) && !this.selectedSite) {
      hints.push({ parts: ['↵'], label: 'open' });
      hints.push({ parts: ['Tab'], label: 'use site' });
    } else if (item.kind === 'action') {
      hints.push({ parts: ['↵'], label: 'run' });
      if (this.selectedSite) {
        hints.push({ parts: ['Tab'], label: 'clear site' });
      }
    } else if (item.kind === 'resource') {
      hints.push({ parts: ['↵'], label: 'open' });
      if (this.selectedSite) {
        hints.push({ parts: ['Tab'], label: 'clear site' });
      }
    } else if (this.selectedSite) {
      hints.push({ parts: ['↵'], label: 'open' });
      hints.push({ parts: ['Tab'], label: 'clear site' });
    } else {
      hints.push({ parts: ['↵'], label: 'open' });
    }

    hints.push({ parts: [this.mod, '/'], label: 'shortcuts' });
    return hints;
  }

  public get sections(): PaletteSection[] {
    const sections: PaletteSection[] = [];

    if (this.selectedSite) {
      if (this.siteActionItems.length) {
        sections.push({ key: 'site-actions', title: 'Site actions', items: this.siteActionItems });
      }
      if (this.resourceItems.length) {
        let title = 'Site resources';
        if (this.activeResource === 'dns') {
          title = 'DNS';
        } else if (this.activeResource === 'ssl') {
          title = 'SSL';
        } else if (this.activeResource === 'mail') {
          title = 'Mailboxes';
        }
        sections.push({ key: 'site-resources', title, items: this.resourceItems });
      }
      return sections;
    }

    if (this.prefs.showRecent) {
      const recent = filterRecent(this.recentItems, this.query);
      if (recent.length) {
        sections.push({ key: 'recent', title: 'Recent', items: recent });
      }
    }

    if (this.prefs.showPages) {
      const pages = filterPages(this.pageCatalog, this.query, (key) => String(this.$t(key)));
      if (pages.length) {
        sections.push({ key: 'pages', title: 'Pages', items: pages });
      }
    }

    if (this.prefs.showConsole) {
      const consoleItems = buildConsoleBridgeItems(this.query, this.hasWebsiteScope);
      if (consoleItems.length) {
        sections.push({ key: 'console', title: 'Console', items: consoleItems });
      }
    }

    if (this.query.trim().length >= 2 || /^\d+$/.test(this.query.trim())) {
      if (this.prefs.showWebsites && this.hasWebsiteScope && this.websiteItems.length) {
        sections.push({ key: 'websites', title: 'Websites', items: this.websiteItems });
      }
      if (this.prefs.showServers && this.hasServerScope && this.serverItems.length) {
        sections.push({ key: 'servers', title: 'Servers', items: this.serverItems });
      }
    }

    return sections;
  }

  public isGridSection(sectionKey: string): boolean {
    return sectionKey === 'pages'
      || sectionKey === 'recent'
      || sectionKey === 'console'
      || sectionKey === 'site-actions';
  }

  public get flatItems(): PaletteItem[] {
    return flattenSections(this.sections);
  }

  public get activeDomId(): string | undefined {
    if (!this.flatItems.length) {
      return undefined;
    }
    return this.domIdFor(this.activeIndex);
  }

  private get resourceDeps(): SiteResourceDeps {
    return {
      websiteRepository: this.websiteRepository,
      dnsRepository: this.dnsRepository,
      certificateRepository: this.certificateRepository,
      mailboxRepository: this.mailboxRepository,
    };
  }

  @Watch('showIdleTyped')
  public onShowIdleTypedChange(show: boolean): void {
    if (show) {
      this.$nextTick(() => this.startIdleTyped());
    } else {
      this.stopIdleTyped();
    }
  }

  @Watch('value')
  public async onOpenChange(open: boolean): Promise<void> {
    if (!open) {
      this.hideChibi();
      this.stopIdleTyped();
      this.clearSearchTimer();
      this.resetNavAccel();
      this.clearSparks();
      this.resetShipAi();
      this.showPrefs = false;
      return;
    }

    this.query = '';
    this.activeIndex = 0;
    this.websiteItems = [];
    this.serverItems = [];
    this.resourceItems = [];
    this.activeResource = null;
    this.websiteError = '';
    this.serverError = '';
    this.resourceError = '';
    this.tripKm = 0;
    this.fireIntensity = 0;
    this.displayKmh = 0;
    this.coasting = false;
    this.resetShipAi();
    this.prefs = loadPalettePrefs();
    this.selectedSite = loadSiteContext();
    this.refreshCatalog();
    this.recentItems = recentToPaletteItems(loadRecentJumps());
    this.refreshSiteActions();
    this.rollChibi();
    await this.ensureServersLoaded(true);
    if (this.selectedSite && !this.selectedSite.hostname && this.selectedSite.originServer) {
      this.selectedSite = {
        ...this.selectedSite,
        hostname: this.resolveHostname(this.selectedSite.originServer),
      };
      saveSiteContext(this.selectedSite);
      this.refreshSiteActions();
    }
    this.$nextTick(() => this.focusInput());
  }

  @Watch('chibiAngry')
  public onChibiAngryChange(angry: boolean): void {
    if (angry) {
      this.triggerChibiAnger();
      return;
    }
    // Mouse removed — settle back to ears unless still hovered
    this.chibiFront = false;
    this.chibiRaised = false;
    this.chibiPetting = false;
  }

  @Watch('query')
  public onQueryWatch(): void {
    if (!this.selectedSite) {
      return;
    }
    this.refreshSiteActions();
    const auto = shouldAutoLoadResource(this.query);
    if (auto) {
      this.runResourceSearch(auto);
    } else if (!this.query.trim()) {
      this.resourceItems = [];
      this.activeResource = null;
      this.resourceError = '';
    }
  }

  public togglePrefs(): void {
    this.showPrefs = !this.showPrefs;
  }

  public persistPrefs(): void {
    savePalettePrefs(this.prefs);
    this.activeIndex = 0;
  }

  public onClearRecent(): void {
    clearRecentJumps();
    this.recentItems = [];
    this.activeIndex = 0;
  }

  public clearSelectedSite(): void {
    this.selectedSite = null;
    saveSiteContext(null);
    this.siteActionItems = [];
    this.resourceItems = [];
    this.activeResource = null;
    this.resourceError = '';
    this.activeIndex = 0;
    this.$nextTick(() => this.focusInput());
  }

  public async onResourceHint(kind: 'dns' | 'ssl' | 'mail'): Promise<void> {
    this.query = kind;
    await this.runResourceSearch(kind);
    this.activeIndex = 0;
    this.$nextTick(() => this.focusInput());
  }

  public displayLabel(item: PaletteItem): string {
    if (item.kind === 'page') {
      if (this.isBrowseMode || !this.query.trim()) {
        return browsePageChipLabel(item, (key) => String(this.$t(key)));
      }
      if (item.meta && item.meta.titleKey) {
        const group = String((item.meta && item.meta.groupLabel) || '');
        const title = String(this.$t(String(item.meta.titleKey)));
        return group ? `${group} · ${title}` : title;
      }
      if (item.label === 'home') {
        return 'Home';
      }
    }
    return item.label;
  }

  public itemTitle(item: PaletteItem): string {
    const parts: string[] = [];
    const label = this.displayLabel(item);
    if (label) {
      parts.push(label);
    }
    if (item.description && item.description !== label) {
      parts.push(item.description);
    }
    if (item.path && parts.indexOf(item.path) === -1) {
      parts.push(item.path);
    }
    if (item.kind === 'recent') {
      const source = item.meta && item.meta.sourceKind;
      if (source) {
        parts.push(`recent ${source}`);
      }
      const at = item.meta && item.meta.at;
      if (typeof at === 'number') {
        parts.push(this.formatRelativeTime(at));
      }
    }
    if (item.kind === 'website' && item.meta) {
      const id = item.meta.id;
      if (id !== undefined && parts.join(' ').indexOf(`#${id}`) === -1) {
        parts.push(`#${id}`);
      }
    }
    return parts.join(' · ');
  }

  public kindLabel(item: PaletteItem): string {
    if (item.kind === 'recent') {
      return String(item.meta && item.meta.sourceKind ? item.meta.sourceKind : 'recent');
    }
    if (item.kind === 'select-site') {
      return 'select';
    }
    return item.kind;
  }

  public defaultIcon(kind: string): string {
    switch (kind) {
      case 'website':
        return 'mdi-web';
      case 'server':
        return 'mdi-server';
      case 'console':
        return 'mdi-console';
      case 'recent':
        return 'mdi-history';
      case 'action':
        return 'mdi-lightning-bolt';
      case 'resource':
        return 'mdi-database-search';
      default:
        return 'mdi-file-document-outline';
    }
  }

  public domIdFor(index: number): string {
    return `global-palette-option-${index}`;
  }

  public flatIndex(sectionKey: string, indexInSection: number): number {
    let offset = 0;
    for (const section of this.sections) {
      if (section.key === sectionKey) {
        return offset + indexInSection;
      }
      offset += section.items.length;
    }
    return indexInSection;
  }

  public onQueryInput(): void {
    this.activeIndex = 0;
    if (this.selectedSite) {
      return;
    }
    this.scheduleEntitySearch();
    this.filterServersLocal();
  }

  public onKeydown(event: KeyboardEvent): void {
    const items = this.flatItems;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (this.showPrefs) {
        this.showPrefs = false;
        return;
      }
      if (this.selectedSite && this.query.trim()) {
        this.query = '';
        return;
      }
      if (this.selectedSite) {
        this.clearSelectedSite();
        return;
      }
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      // Always keep focus in the palette input; bare Tab would blur it.
      event.preventDefault();
      if (this.selectedSite) {
        this.clearSelectedSite();
        return;
      }
      const item = items[this.activeIndex];
      if (item && isSiteSelectable(item)) {
        this.selectSiteFromItem(item);
      }
      return;
    }

    // Never treat Space as a selection shortcut in the palette.
    if (event.key === ' ' || event.code === 'Space') {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.navigateHeld('ArrowDown', 'down');
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.navigateHeld('ArrowUp', 'up');
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateHeld('ArrowRight', 'right');
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigateHeld('ArrowLeft', 'left');
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[this.activeIndex];
      if (item) {
        this.selectItem(item);
      }
    }
  }

  public onKeyup(event: KeyboardEvent): void {
    if (
      event.key === 'ArrowDown'
      || event.key === 'ArrowUp'
      || event.key === 'ArrowLeft'
      || event.key === 'ArrowRight'
    ) {
      if (this.heldArrow === event.key) {
        if (this.driveEngaged) {
          this.beginCoastDown();
        } else {
          // Short tap / brief hold — only navigation, no drive session.
          this.heldArrow = null;
          this.driveDirection = null;
          this.holdStartedAt = 0;
        }
      }
    }
  }

  public async selectItem(item: PaletteItem): Promise<void> {
    if (item.kind === 'action' && item.meta && item.meta.siteAction) {
      const action = String(item.meta.siteAction);
      if (action === 'dns' || action === 'ssl' || action === 'mail') {
        await this.runResourceSearch(action as 'dns' | 'ssl' | 'mail');
        this.activeIndex = 0;
        return;
      }
    }

    const navItem = this.toNavigableItem(item);
    if (navItem.kind === 'website' || navItem.kind === 'server' || navItem.kind === 'page' || navItem.kind === 'console') {
      pushRecentJump({
        ...navItem,
        label: item.kind === 'recent' ? item.label : this.displayLabel(item),
      });
    }

    if (!navItem.routeName && !navItem.path) {
      return;
    }

    this.close();

    try {
      if (navItem.routeName) {
        await this.$router.push({
          name: navItem.routeName,
          params: navItem.routeParams || {},
          query: navItem.routeQuery || {},
        });
        return;
      }
      if (navItem.path) {
        await this.$router.push(navItem.path);
      }
    } catch (err) {
      // ignore
    }
  }

  public close(): void {
    this.$emit('input', false);
  }

  private selectSiteFromItem(item: PaletteItem): void {
    const nav = this.toNavigableItem(item);
    const id = Number(nav.meta && nav.meta.id);
    if (!id) {
      return;
    }
    const originServer = String((nav.meta && nav.meta.originServer) || '');
    const hostname = String((nav.meta && nav.meta.hostname) || this.resolveHostname(originServer) || '');
    const domain = String(
      (nav.meta && nav.meta.domain)
      || nav.label
      || item.label.replace(/^Select ·\s*/, ''),
    );
    this.selectedSite = {
      id,
      domain,
      status: String((nav.meta && nav.meta.status) || ''),
      originServer,
      hostname: hostname || undefined,
    };
    saveSiteContext(this.selectedSite);
    this.query = '';
    this.websiteItems = [];
    this.resourceItems = [];
    this.activeResource = null;
    this.refreshSiteActions();
    this.activeIndex = 0;
    this.$nextTick(() => this.focusInput());
  }

  private formatRelativeTime(at: number): string {
    const delta = Math.max(0, Date.now() - at);
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    if (delta < minute) {
      return 'just now';
    }
    if (delta < hour) {
      const n = Math.floor(delta / minute);
      return `${n}m ago`;
    }
    if (delta < day) {
      const n = Math.floor(delta / hour);
      return `${n}h ago`;
    }
    const n = Math.floor(delta / day);
    return `${n}d ago`;
  }

  private refreshSiteActions(): void {
    if (!this.selectedSite) {
      this.siteActionItems = [];
      return;
    }
    this.siteActionItems = buildSiteActionItems(this.selectedSite, this.query).slice(0, 5);
  }

  private async runResourceSearch(kind: 'dns' | 'ssl' | 'mail'): Promise<void> {
    if (!this.selectedSite) {
      return;
    }
    const seq = ++this.resourceSeq;
    this.loadingResources = true;
    this.resourceError = '';
    this.activeResource = kind;

    try {
      let items: PaletteItem[] = [];
      if (kind === 'dns') {
        items = await loadSiteDnsItems(this.resourceDeps, this.selectedSite);
      } else if (kind === 'ssl') {
        items = await loadSiteSslItems(this.resourceDeps, this.selectedSite);
      } else {
        items = await loadSiteMailItems(this.resourceDeps, this.selectedSite);
      }
      if (seq !== this.resourceSeq) {
        return;
      }
      this.resourceItems = items.slice(0, 5);
    } catch (err) {
      if (seq !== this.resourceSeq) {
        return;
      }
      this.resourceItems = [];
      this.resourceError = `Failed to load ${kind} for site`;
    } finally {
      if (seq === this.resourceSeq) {
        this.loadingResources = false;
      }
    }
  }

  private toNavigableItem(item: PaletteItem): PaletteItem {
    if (item.kind !== 'recent') {
      return item;
    }
    const sourceKind = item.meta && item.meta.sourceKind ? String(item.meta.sourceKind) : 'page';
    return {
      ...item,
      id: item.id.replace(/^recent:/, ''),
      kind: sourceKind as PaletteItem['kind'],
    };
  }

  private refreshCatalog(): void {
    const scopes: string[] = this.$store.state.user.scopes || [];
    const isAdmin = !!this.$store.state.user.isAdmin;
    this.pageCatalog = buildPageCatalog(scopes, isAdmin).map((item) => ({
      ...item,
      label: item.meta && item.meta.titleKey ? String(item.meta.titleKey) : item.label,
    }));
  }

  private focusInput(): void {
    const el = this.$refs.input as HTMLInputElement | undefined;
    if (el) {
      el.focus();
      el.select();
    }
  }

  private clearChibiTimers(): void {
    if (this.chibiAppearTimer !== null) {
      window.clearTimeout(this.chibiAppearTimer);
      this.chibiAppearTimer = null;
    }
    if (this.chibiFrontTimer !== null) {
      window.clearTimeout(this.chibiFrontTimer);
      this.chibiFrontTimer = null;
    }
    if (this.chibiPetTimer !== null) {
      window.clearTimeout(this.chibiPetTimer);
      this.chibiPetTimer = null;
    }
    if (this.chibiReactionTimer !== null) {
      window.clearTimeout(this.chibiReactionTimer);
      this.chibiReactionTimer = null;
    }
  }

  private rollChibi(): void {
    this.hideChibi();
    // Mount hidden, then peek ears after a short delay
    this.chibiVisible = true;
    this.chibiEars = false;
    this.chibiAppearTimer = window.setTimeout(() => {
      this.chibiEars = true;
      this.chibiAppearTimer = null;
    }, CHIBI_APPEAR_MS);
  }

  public onChibiEnter(): void {
    if (!this.chibiVisible || !this.chibiEars) {
      return;
    }
    this.chibiRaised = true;
    if (this.chibiFrontTimer !== null) {
      window.clearTimeout(this.chibiFrontTimer);
    }
    // Stay behind while rising; only pop to front after the motion finishes
    this.chibiFrontTimer = window.setTimeout(() => {
      if (this.chibiRaised) {
        this.chibiFront = true;
      }
      this.chibiFrontTimer = null;
    }, CHIBI_RAISE_MS);
  }

  public onChibiLeave(): void {
    if (this.chibiAngry) {
      // Stay popped up and furious while a forbidden word is in the query
      return;
    }
    if (this.chibiFrontTimer !== null) {
      window.clearTimeout(this.chibiFrontTimer);
      this.chibiFrontTimer = null;
    }
    // Immediately drop behind again, then duck to ears-only
    this.chibiFront = false;
    this.chibiRaised = false;
    this.chibiPetting = false;
    this.chibiReaction = '';
    if (this.chibiPetTimer !== null) {
      window.clearTimeout(this.chibiPetTimer);
      this.chibiPetTimer = null;
    }
    if (this.chibiReactionTimer !== null) {
      window.clearTimeout(this.chibiReactionTimer);
      this.chibiReactionTimer = null;
    }
  }

  private triggerChibiAnger(): void {
    if (!this.chibiVisible) {
      return;
    }
    // Make sure ears are out so the rage is visible
    if (!this.chibiEars) {
      if (this.chibiAppearTimer !== null) {
        window.clearTimeout(this.chibiAppearTimer);
        this.chibiAppearTimer = null;
      }
      this.chibiEars = true;
    }
    this.chibiRaised = true;
    this.chibiFront = true;

    this.chibiPetSeq += 1;
    const seq = this.chibiPetSeq;
    this.chibiReactionTone = CHIBI_ANGRY_TONES[
      Math.floor(Math.random() * CHIBI_ANGRY_TONES.length)
    ];
    this.chibiReactionFx = 'pop';
    this.chibiReaction = CHIBI_ANGRY_REACTIONS[
      Math.floor(Math.random() * CHIBI_ANGRY_REACTIONS.length)
    ];
    if (this.chibiReactionTimer !== null) {
      window.clearTimeout(this.chibiReactionTimer);
    }
    this.chibiReactionTimer = window.setTimeout(() => {
      if (seq === this.chibiPetSeq) {
        this.chibiReaction = '';
      }
      this.chibiReactionTimer = null;
    }, 1600);
  }

  public onPetChibi(): void {
    if (!this.chibiFront && !this.chibiAngry) {
      return;
    }

    this.chibiPetSeq += 1;
    const seq = this.chibiPetSeq;

    if (this.chibiAngry) {
      this.chibiReactionTone = CHIBI_ANGRY_TONES[
        Math.floor(Math.random() * CHIBI_ANGRY_TONES.length)
      ];
      this.chibiReactionFx = Math.random() > 0.5 ? 'pop' : 'wiggle';
      this.chibiReaction = CHIBI_ANGRY_REACTIONS[
        Math.floor(Math.random() * CHIBI_ANGRY_REACTIONS.length)
      ];
    } else {
      this.chibiReactionTone = CHIBI_REACTION_TONES[
        Math.floor(Math.random() * CHIBI_REACTION_TONES.length)
      ];
      this.chibiReactionFx = Math.random() > 0.5
        ? 'pop'
        : (Math.random() > 0.5 ? 'wiggle' : 'float');
      this.chibiReaction = CHIBI_PET_REACTIONS[
        Math.floor(Math.random() * CHIBI_PET_REACTIONS.length)
      ];
    }

    // Keep pet class on and hard-restart the CSS animation so spam-clicks replay fully
    this.chibiPetting = true;
    this.$nextTick(() => {
      const img = this.$refs.chibiImg as HTMLElement | undefined;
      if (!img || seq !== this.chibiPetSeq) {
        return;
      }
      img.style.animation = 'none';
      // Force reflow so the browser accepts a fresh animation start
      void img.offsetWidth;
      img.style.animation = '';
    });

    if (this.chibiPetTimer !== null) {
      window.clearTimeout(this.chibiPetTimer);
    }
    if (this.chibiReactionTimer !== null) {
      window.clearTimeout(this.chibiReactionTimer);
    }
    this.chibiPetTimer = window.setTimeout(() => {
      if (seq === this.chibiPetSeq) {
        this.chibiPetting = false;
      }
      this.chibiPetTimer = null;
    }, 520);
    this.chibiReactionTimer = window.setTimeout(() => {
      if (seq === this.chibiPetSeq) {
        this.chibiReaction = '';
      }
      this.chibiReactionTimer = null;
    }, 1300);
  }

  private hideChibi(): void {
    this.clearChibiTimers();
    this.chibiVisible = false;
    this.chibiEars = false;
    this.chibiRaised = false;
    this.chibiFront = false;
    this.chibiPetting = false;
    this.chibiReaction = '';
  }

  private startIdleTyped(): void {
    this.stopIdleTyped();
    if (!this.showIdleTyped) {
      return;
    }

    const el = this.$refs.input as HTMLInputElement | undefined;
    if (!el) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      el.placeholder = IDLE_TYPED_STRINGS[0];
      return;
    }

    // Type into the real input's placeholder — no overlay, no second cursor.
    this.idleTyped = new Typed(el, {
      strings: IDLE_TYPED_STRINGS,
      attr: 'placeholder',
      typeSpeed: 42,
      backSpeed: 22,
      backDelay: 1400,
      startDelay: 350,
      loop: true,
      smartBackspace: true,
      showCursor: false,
      bindInputFocusEvents: false,
    });
  }

  private stopIdleTyped(): void {
    if (this.idleTyped) {
      this.idleTyped.destroy();
      this.idleTyped = null;
    }
    const el = this.$refs.input as HTMLInputElement | undefined;
    if (!el) {
      return;
    }
    // Restore static placeholder when not running the idle animation.
    el.placeholder = this.showIdleTyped ? '' : this.inputPlaceholder;
  }

  public beforeDestroy(): void {
    this.hideChibi();
    this.stopIdleTyped();
    this.clearSearchTimer();
    this.resetNavAccel();
    this.clearSparks();
    this.stopHyperspace();
    this.resetShipAi();
    this.destroyEngineAudio();
  }

  private scheduleEntitySearch(): void {
    this.clearSearchTimer();
    const q = this.query.trim();
    if (!(q.length >= 2 || /^\d+$/.test(q))) {
      this.websiteItems = [];
      this.loadingWebsites = false;
      return;
    }
    this.searchTimer = window.setTimeout(() => {
      this.runWebsiteSearch(q);
    }, 250);
  }

  private clearSearchTimer(): void {
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  }

  private resolveHostname(originServer: string): string {
    if (!originServer) {
      return '';
    }
    if (this.hostnameMap[originServer]) {
      return this.hostnameMap[originServer];
    }
    if (this.serversCache) {
      const found = this.serversCache.find((server) => server._id === originServer);
      if (found) {
        this.hostnameMap[originServer] = found.hostname;
        return found.hostname;
      }
    }
    return '';
  }

  private mapWebsiteItem(site: Website): PaletteItem {
    const hostname = this.resolveHostname(site.originServer || '');
    const serverLabel = hostname || site.originServer || '—';
    return {
      id: `website:${site.id}`,
      kind: 'website',
      label: site.domain,
      description: `#${site.id} · ${site.status} · ${serverLabel}${hostname ? '' : (site.originServer ? ' (resolving…)' : '')}`,
      icon: 'mdi-web',
      routeName: 'website_view',
      routeParams: { id: String(site.id) },
      keywords: [site.domain, String(site.id), site.originServer || '', hostname, site.status],
      meta: {
        id: site.id,
        status: site.status,
        originServer: site.originServer,
        hostname,
        domain: site.domain,
      },
    };
  }

  private async runWebsiteSearch(q: string): Promise<void> {
    if (!this.hasWebsiteScope || !this.prefs.showWebsites) {
      this.websiteItems = [];
      return;
    }
    const seq = ++this.searchSeq;
    this.loadingWebsites = true;
    this.websiteError = '';
    try {
      const response = await this.websiteRepository.searchWebsites(1, q, undefined, undefined, undefined, 8);
      if (seq !== this.searchSeq) {
        return;
      }
      const sites: Website[] = (response.Websites || []).slice(0, 8);
      this.websiteItems = sites.map((site) => this.mapWebsiteItem(site));
      this.resolveHostnamesForSites(sites, seq);
    } catch (err) {
      if (seq !== this.searchSeq) {
        return;
      }
      this.websiteItems = [];
      this.websiteError = 'Website search failed';
    } finally {
      if (seq === this.searchSeq) {
        this.loadingWebsites = false;
      }
    }
  }

  private async resolveHostnamesForSites(sites: Website[], seq: number): Promise<void> {
    const missing = sites
      .map((site) => site.originServer)
      .filter((id) => !!id && !this.resolveHostname(id));
    const unique: string[] = [];
    missing.forEach((id) => {
      if (unique.indexOf(id) === -1) {
        unique.push(id);
      }
    });
    if (!unique.length) {
      return;
    }
    try {
      const servers = await this.serverRepository.getServersByIds(unique);
      (servers || []).forEach((server) => {
        this.hostnameMap[server._id] = server.hostname;
      });
      if (seq !== this.searchSeq) {
        return;
      }
      this.websiteItems = sites.map((site) => this.mapWebsiteItem(site));
    } catch {
      // keep id fallback
    }
  }

  private async ensureServersLoaded(forHostnames = false): Promise<void> {
    if (!this.hasServerScope && !(forHostnames && this.hasWebsiteScope)) {
      return;
    }
    if (this.serversCache) {
      this.serversCache.forEach((server) => {
        this.hostnameMap[server._id] = server.hostname;
      });
      this.filterServersLocal();
      return;
    }
    if (this.serversLoading) {
      await this.serversLoading;
      this.filterServersLocal();
      return;
    }
    this.serversLoading = (async () => {
      try {
        this.serverError = '';
        this.serversCache = await this.serverRepository.getAllServers();
        (this.serversCache || []).forEach((server) => {
          this.hostnameMap[server._id] = server.hostname;
        });
      } catch (err) {
        if (this.prefs.showServers) {
          this.serverError = 'Server list failed to load';
        }
        this.serversCache = [];
      } finally {
        this.serversLoading = null;
      }
    })();
    await this.serversLoading;
    this.filterServersLocal();
  }

  private filterServersLocal(): void {
    if (!this.hasServerScope || !this.prefs.showServers || !this.serversCache) {
      this.serverItems = [];
      return;
    }
    const all: PaletteItem[] = this.serversCache.map((server) => {
      const ips = (server.config && server.config.ips) || [];
      return {
        id: `server:${server._id}`,
        kind: 'server' as const,
        label: server.hostname,
        description: `${server.type} · ${server.status} · ${server._id}`,
        icon: 'mdi-server',
        routeName: 'server_view',
        routeParams: { server: server._id },
        keywords: [server.hostname, server._id, server.type, server.status, ...ips],
        meta: {
          id: server._id,
          hostname: server.hostname,
        },
      };
    });
    this.serverItems = filterServers(all, this.query);
  }

  private scrollActiveIntoView(): void {
    this.$nextTick(() => {
      const el = document.getElementById(this.domIdFor(this.activeIndex));
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  private resetNavAccel(): void {
    this.heldArrow = null;
    this.driveDirection = null;
    this.holdStartedAt = 0;
    this.driveEngaged = false;
    this.navAccel = 0;
    this.hyperdrive = false;
    this.hyperdriveKmh = 0;
    this.displayKmh = 0;
    this.coasting = false;
    this.fireIntensity = 0;
    this.stopDriveLoop();
    this.stopEngineAudio();
    this.stopHyperspace();
  }

  private beginCoastDown(): void {
    if (this.coasting) {
      return;
    }
    const wasHyperdrive = this.hyperdrive;
    const speed = this.liveDriveKmh();
    this.displayKmh = Math.max(speed, this.displayKmh);
    this.heldArrow = null;
    this.driveDirection = null;
    this.holdStartedAt = 0;
    this.driveEngaged = false;
    this.hyperdrive = false;
    this.coasting = true;
    this.stopEngineAudio();
    this.stopHyperspace();
    // Keep the drive RAF alive so speed / fire can ease down.
    this.ensureDriveLoop();
    this.onDriveSessionExit(wasHyperdrive);
  }

  private ensureEngineAudio(): HTMLAudioElement {
    if (!this.engineAudio) {
      const audio = new Audio(ENGINE_SRC);
      audio.preload = 'auto';
      audio.volume = 0.65;
      this.engineAudio = audio;
    }
    return this.engineAudio;
  }

  private startEngineAudio(): void {
    const audio = this.ensureEngineAudio();
    try {
      audio.pause();
      audio.currentTime = 0;
      const play = audio.play();
      if (play && typeof play.catch === 'function') {
        play.catch(() => { /* autoplay / missing file */ });
      }
    } catch (_err) {
      // Ignore playback failures (autoplay policy, missing asset).
    }
  }

  private stopEngineAudio(): void {
    if (!this.engineAudio) {
      return;
    }
    try {
      this.engineAudio.pause();
      this.engineAudio.currentTime = 0;
    } catch (_err) {
      // ignore
    }
  }

  private destroyEngineAudio(): void {
    this.stopEngineAudio();
    this.engineAudio = null;
  }

  private stopDriveLoop(): void {
    if (this.driveRaf !== null) {
      window.cancelAnimationFrame(this.driveRaf);
      this.driveRaf = null;
    }
    this.lastDriveTs = 0;
  }

  private ensureDriveLoop(): void {
    if (this.driveRaf !== null) {
      return;
    }
    const tick = (ts: number) => {
      if (!this.heldArrow && !this.coasting) {
        this.driveRaf = null;
        this.lastDriveTs = 0;
        return;
      }

      const dt = this.lastDriveTs
        ? Math.min(0.05, (ts - this.lastDriveTs) / 1000)
        : 0.016;
      this.lastDriveTs = ts;

      if (this.coasting) {
        this.updateCoastDown(dt);
        this.updateFireIntensity(dt);
        this.driveRaf = window.requestAnimationFrame(tick);
        return;
      }

      this.syncDriveFromAudio();
      this.updateHyperdriveKmh(ts, dt);
      this.displayKmh = this.liveDriveKmh();
      this.updateFireIntensity(dt);
      this.updateShipAi(dt);

      // Odometer tracks live speed (km = km/h × hours), so warp km/h racks up trip fast.
      if (this.driveDirection && (this.hyperdrive || this.navAccel >= NAV_SPARK_AT)) {
        this.addTripFromSpeed(dt);
      }

      if (this.hyperdrive && this.driveDirection) {
        // Warp scroll: several hops per frame while the hyperdrive cue plays.
        const steps = 4 + Math.floor(Math.random() * 4);
        for (let i = 0; i < steps; i += 1) {
          this.moveActive(this.driveDirection, i === steps - 1);
        }
        this.spawnSparksFromActive();
      }

      this.driveRaf = window.requestAnimationFrame(tick);
    };
    this.driveRaf = window.requestAnimationFrame(tick);
  }

  private updateCoastDown(dt: number): void {
    // Higher speeds bleed off faster so warp numbers don't take forever.
    const decay = 1.35 + Math.min(10, this.displayKmh / 25000);
    this.displayKmh = Math.max(0, this.displayKmh - (this.displayKmh * decay * dt));
    if (this.displayKmh < 12) {
      this.displayKmh = Math.max(0, this.displayKmh - (28 * dt));
    }
    this.navAccel = Math.min(
      NAV_ACCEL_MAX,
      (this.displayKmh / FIRE_FULL_KMH) * NAV_ACCEL_MAX,
    );
    this.hyperdriveKmh = this.displayKmh;

    if (this.displayKmh <= 4) {
      this.coasting = false;
      this.displayKmh = 0;
      this.navAccel = 0;
      this.hyperdriveKmh = 0;
      this.fireIntensity = 0;
      this.clearSparks();
    }
  }

  private syncDriveFromAudio(): void {
    const audio = this.engineAudio;
    let t = 0;
    if (audio) {
      t = audio.currentTime || 0;
      // When the track ends it pauses — seek past the boom and keep playing.
      const nearEnd = !!(audio.duration && t >= audio.duration - 0.12);
      if (this.heldArrow && (audio.ended || nearEnd)) {
        try {
          audio.currentTime = ENGINE_LOOP_AT;
          t = ENGINE_LOOP_AT;
          if (audio.paused || audio.ended) {
            const play = audio.play();
            if (play && typeof play.catch === 'function') {
              play.catch(() => { /* ignore */ });
            }
          }
        } catch (_err) {
          // ignore seek / play failures
        }
      }
    }

    if (t >= ENGINE_HYPERDRIVE_AT) {
      if (!this.hyperdrive) {
        // Break the 320 redline the moment the hyperdrive cue hits.
        this.hyperdriveKmh = Math.max(320, this.displayKmh || 320) + Math.random() * 40;
        this.hyperdrive = true;
        this.onHyperdriveEnter();
        this.$nextTick(() => this.ensureHyperspace());
      } else {
        this.hyperdrive = true;
      }
      this.navAccel = NAV_ACCEL_MAX;
      return;
    }

    if (this.hyperdrive) {
      this.stopHyperspace();
      // Dropped out of the cue while still holding — keep AI going, no exit toast yet.
      this.hyperdrive = false;
    }
    // Slow climb: 0 → max over the pre-hyperdrive spool (0–14s).
    this.navAccel = (t / ENGINE_HYPERDRIVE_AT) * NAV_ACCEL_MAX;
  }

  private navigateHeld(
    key: string,
    direction: 'up' | 'down' | 'left' | 'right',
  ): void {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();

    if (this.coasting) {
      // Re-throttle cancels the coast and starts a fresh spool.
      const wasExiting = this.aiExiting;
      this.coasting = false;
      this.displayKmh = 0;
      this.navAccel = 0;
      this.hyperdrive = false;
      this.hyperdriveKmh = 0;
      this.heldArrow = null;
      this.driveEngaged = false;
      this.holdStartedAt = 0;
      if (wasExiting) {
        this.resetShipAi();
      }
    }

    if (this.heldArrow !== key) {
      this.heldArrow = key;
      this.holdStartedAt = now;
      this.driveEngaged = false;
      this.driveDirection = direction;
      this.navAccel = 0;
      this.hyperdrive = false;
      this.hyperdriveKmh = 0;
      this.displayKmh = 0;
      // Tap / first press: move once. Drive mode waits for a real hold.
      this.moveActive(direction, true);
      return;
    }

    this.driveDirection = direction;

    if (!this.driveEngaged) {
      if (now - this.holdStartedAt < DRIVE_HOLD_MS) {
        // Still under the hold threshold — normal key-repeat navigation only.
        this.moveActive(direction, true);
        return;
      }
      this.engageDrive();
    }

    this.ensureDriveLoop();
    this.syncDriveFromAudio();

    const total = this.flatItems.length;
    if (!total) {
      return;
    }

    if (this.hyperdrive) {
      // Extra burst on key-repeat while already in hyperdrive.
      const steps = 6 + Math.floor(Math.random() * 5);
      for (let i = 0; i < steps; i += 1) {
        this.moveActive(direction, i === steps - 1);
      }
      this.spawnSparksFromActive();
      return;
    }

    // Pre-hyperdrive: one hop per key event; meter/km follow the engine spool.
    this.moveActive(direction, true);
    if (this.navAccel >= NAV_SPARK_AT) {
      this.spawnSparksFromActive();
    }
  }

  private engageDrive(): void {
    if (this.driveEngaged) {
      return;
    }
    this.driveEngaged = true;
    this.navAccel = 0;
    this.hyperdrive = false;
    this.hyperdriveKmh = 0;
    this.displayKmh = 0;
    this.startEngineAudio();
    this.ensureDriveLoop();
  }

  private liveDriveKmh(): number {
    if (this.hyperdrive) {
      return Math.max(320, this.hyperdriveKmh);
    }
    return 40 + ((this.navAccel / NAV_ACCEL_MAX) * 279);
  }

  private currentDriveKmh(): number {
    if (this.coasting) {
      return this.displayKmh;
    }
    return this.liveDriveKmh();
  }

  private addTripFromSpeed(dt: number): void {
    const kmh = this.currentDriveKmh();
    // Convert km/h → km for this frame; arcade scale so warp speeds feel huge.
    this.tripKm += (kmh / 3600) * dt * 12;
  }

  private updateFireIntensity(dt: number): void {
    const kmh = this.currentDriveKmh();
    let target = 0;
    if (this.hyperdrive) {
      // Full fire stays on with the hyperspace effect.
      target = 1;
    } else if (kmh >= FIRE_START_KMH) {
      target = Math.min(1, (kmh - FIRE_START_KMH) / (FIRE_FULL_KMH - FIRE_START_KMH));
    }
    // Ease in slowly so the border grows rather than popping on.
    const rate = target > this.fireIntensity ? 0.55 : 1.8;
    const next = this.fireIntensity + ((target - this.fireIntensity) * Math.min(1, dt * rate));
    this.fireIntensity = next < 0.01 && target === 0 ? 0 : next;
  }

  private updateShipAi(dt: number): void {
    if (this.aiStarted || this.aiExiting || this.coasting) {
      return;
    }
    const kmh = this.currentDriveKmh();
    if (kmh < FIRE_START_KMH) {
      this.aiAboveMs = 0;
      return;
    }
    this.aiAboveMs += dt * 1000;
    if (this.aiAboveMs >= AI_CHAT_DELAY_MS) {
      this.aiStarted = true;
      this.queueNextAiLine();
    }
  }

  private onHyperdriveEnter(): void {
    // Keep prior chatter; announce warp and continue the stream afterward.
    this.aiExiting = false;
    this.aiStarted = true;
    this.pushAiLine(SHIP_AI_ENTER);
  }

  private onDriveSessionExit(wasHyperdrive: boolean): void {
    if (this.aiExiting) {
      return;
    }
    // Exit toast even if we never hit hyperdrive (cruise / 100+ session).
    const shouldAnnounce = wasHyperdrive
      || this.aiStarted
      || this.displayKmh >= FIRE_START_KMH;
    if (!shouldAnnounce) {
      return;
    }
    this.aiExiting = true;
    this.aiStarted = true;
    this.clearAiTypeTimer();
    this.clearAiGapTimer();
    this.aiBubbles.splice(0, this.aiBubbles.length);
    this.pushAiLine(wasHyperdrive ? SHIP_AI_EXIT : SHIP_AI_EXIT_CRUISE);
  }

  private queueNextAiLine(): void {
    if (!this.aiStarted || this.aiExiting || !SHIP_AI_LINES.length) {
      return;
    }
    const full = SHIP_AI_LINES[this.aiLineIdx % SHIP_AI_LINES.length];
    this.aiLineIdx += 1;
    this.pushAiLine(full);
  }

  private pushAiLine(full: string): void {
    this.clearAiTypeTimer();
    this.clearAiGapTimer();

    const bubble: AiBubble = {
      id: ++this.aiSeq,
      full,
      shown: '',
      done: false,
      fresh: true,
    };
    this.aiBubbles.push(bubble);
    while (this.aiBubbles.length > AI_MAX_BUBBLES) {
      this.aiBubbles.shift();
    }

    const bubbleId = bubble.id;
    let i = 0;
    this.aiTypeTimer = window.setInterval(() => {
      const idx = this.aiBubbles.findIndex((b) => b.id === bubbleId);
      if (idx < 0) {
        this.clearAiTypeTimer();
        return;
      }
      i += 1;
      this.$set(this.aiBubbles[idx], 'shown', full.slice(0, i));
      if (i >= full.length) {
        this.$set(this.aiBubbles[idx], 'done', true);
        this.$set(this.aiBubbles[idx], 'fresh', false);
        this.clearAiTypeTimer();
        if (this.aiExiting) {
          this.aiGapTimer = window.setTimeout(() => {
            this.aiGapTimer = null;
            this.resetShipAi();
          }, 1600);
          return;
        }
        this.aiGapTimer = window.setTimeout(() => {
          this.aiGapTimer = null;
          this.queueNextAiLine();
        }, AI_GAP_MS);
      }
    }, AI_TYPE_MS);
  }

  private resetShipAi(): void {
    this.clearAiTypeTimer();
    this.clearAiGapTimer();
    this.aiAboveMs = 0;
    this.aiStarted = false;
    this.aiExiting = false;
    this.aiLineIdx = 0;
    this.aiBubbles.splice(0, this.aiBubbles.length);
  }

  private clearAiTypeTimer(): void {
    if (this.aiTypeTimer !== null) {
      window.clearInterval(this.aiTypeTimer);
      this.aiTypeTimer = null;
    }
  }

  private clearAiGapTimer(): void {
    if (this.aiGapTimer !== null) {
      window.clearTimeout(this.aiGapTimer);
      this.aiGapTimer = null;
    }
  }

  private updateHyperdriveKmh(_ts: number, dt: number): void {
    if (!this.hyperdrive) {
      return;
    }
    // Accelerate hard past redline — thousands of km/h within seconds.
    const climb = 1800 + (this.hyperdriveKmh * 0.55) + (Math.random() * 2200);
    this.hyperdriveKmh += dt * climb;
    // Occasional flicker so the digits feel unstable at warp.
    if (Math.random() < 0.12) {
      this.hyperdriveKmh += (Math.random() - 0.35) * Math.max(80, this.hyperdriveKmh * 0.04);
    }
    if (this.hyperdriveKmh < 320) {
      this.hyperdriveKmh = 320;
    }
  }

  private ensureHyperspace(): void {
    if (this.hyperRaf !== null || !this.hyperdrive) {
      return;
    }
    this.initHyperStars();
    const tick = () => {
      if (!this.hyperdrive) {
        this.hyperRaf = null;
        this.clearHyperspaceCanvas();
        return;
      }
      this.drawHyperspace();
      this.hyperRaf = window.requestAnimationFrame(tick);
    };
    this.hyperRaf = window.requestAnimationFrame(tick);
  }

  private stopHyperspace(): void {
    if (this.hyperRaf !== null) {
      window.cancelAnimationFrame(this.hyperRaf);
      this.hyperRaf = null;
    }
    this.hyperStars = [];
    this.clearHyperspaceCanvas();
  }

  private initHyperStars(): void {
    const stars: HyperStar[] = [];
    for (let i = 0; i < HYPER_STAR_COUNT; i += 1) {
      stars.push(this.spawnHyperStar(true));
    }
    this.hyperStars = stars;
  }

  private spawnHyperStar(scatter: boolean): HyperStar {
    return {
      angle: Math.random() * Math.PI * 2,
      dist: scatter ? Math.random() * 180 : Math.random() * 12,
      speed: 2.2 + Math.random() * 5.5,
      len: 10 + Math.random() * 28,
      bright: 0.35 + Math.random() * 0.65,
    };
  }

  private clearHyperspaceCanvas(): void {
    const canvas = this.$refs.hyperspace as HTMLCanvasElement | undefined;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  private drawHyperspace(): void {
    const canvas = this.$refs.hyperspace as HTMLCanvasElement | undefined;
    if (!canvas) {
      return;
    }
    const panel = canvas.parentElement;
    if (!panel) {
      return;
    }
    const w = panel.clientWidth;
    const h = panel.clientHeight;
    if (!w || !h) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      ctx.fillStyle = '#05070f';
      ctx.fillRect(0, 0, w, h);
    }

    // Soft trail so streaks read as warp lines, not dots.
    ctx.fillStyle = 'rgba(5, 7, 15, 0.38)';
    ctx.fillRect(0, 0, w, h);

    const cx = w * 0.5;
    const cy = h * 0.5;
    const maxDist = Math.hypot(cx, cy) + 40;
    const warp = 1 + Math.min(10, this.hyperdriveKmh / 18000);
    const stars = this.hyperStars;

    for (let i = 0; i < stars.length; i += 1) {
      const star = stars[i];
      const prev = star.dist;
      star.dist += star.speed * warp;
      const streak = star.len * (0.6 + warp * 0.55);
      const x0 = cx + Math.cos(star.angle) * prev;
      const y0 = cy + Math.sin(star.angle) * prev;
      const x1 = cx + Math.cos(star.angle) * star.dist;
      const y1 = cy + Math.sin(star.angle) * star.dist;
      const xBack = cx + Math.cos(star.angle) * Math.max(0, star.dist - streak);
      const yBack = cy + Math.sin(star.angle) * Math.max(0, star.dist - streak);

      const alpha = Math.min(1, star.bright * (0.45 + (star.dist / maxDist) * 0.9));
      ctx.strokeStyle = `rgba(210, 230, 255, ${alpha})`;
      ctx.lineWidth = star.dist > maxDist * 0.55 ? 2 : 1.25;
      ctx.beginPath();
      ctx.moveTo(xBack, yBack);
      ctx.lineTo(x1, y1);
      ctx.stroke();

      // Hot tip flash near the leading edge.
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.fillRect(x1 - 0.75, y1 - 0.75, 1.5, 1.5);

      if (star.dist > maxDist || (x0 < -20 && x1 < -20) || (y0 < -20 && y1 < -20)) {
        stars[i] = this.spawnHyperStar(false);
      }
    }
  }

  private spawnSparksFromActive(): void {
    const el = document.getElementById(this.domIdFor(this.activeIndex));
    const panel = this.$el && (this.$el.querySelector('.global-palette__panel') as HTMLElement | null);
    if (!el || !panel) {
      return;
    }
    const er = el.getBoundingClientRect();
    const pr = panel.getBoundingClientRect();
    const hot = this.hyperdrive || this.navAccel >= NAV_ACCEL_MAX;
    const count = hot ? 4 : 2;
    const next = this.sparks.slice();
    for (let i = 0; i < count; i += 1) {
      next.push({
        id: ++this.sparkSeq,
        x: (er.left - pr.left) + (Math.random() * Math.max(er.width, 8)),
        y: (er.bottom - pr.top) - 1,
        vx: (Math.random() - 0.5) * 0.55,
        vy: 0.25 + Math.random() * 0.35,
        life: 1,
        size: hot ? 3 : 2,
        hot,
      });
    }
    if (next.length > NAV_SPARK_MAX) {
      this.sparks = next.slice(next.length - NAV_SPARK_MAX);
    } else {
      this.sparks = next;
    }
    this.ensureSparkLoop();
  }

  private ensureSparkLoop(): void {
    if (this.sparkRaf !== null) {
      return;
    }
    const tick = (ts: number) => {
      const dt = this.lastSparkTs ? Math.min(32, ts - this.lastSparkTs) / 16.67 : 1;
      this.lastSparkTs = ts;
      const next: NavSpark[] = [];
      for (let i = 0; i < this.sparks.length; i += 1) {
        const spark = this.sparks[i];
        const life = spark.life - (0.0065 * dt);
        if (life <= 0) {
          continue;
        }
        next.push({
          id: spark.id,
          x: spark.x + (spark.vx * dt),
          y: spark.y + (spark.vy * dt),
          vx: spark.vx * 0.995,
          vy: spark.vy + (0.018 * dt),
          life,
          size: spark.size,
          hot: spark.hot,
        });
      }
      this.sparks = next;
      if (this.sparks.length) {
        this.sparkRaf = window.requestAnimationFrame(tick);
      } else {
        this.sparkRaf = null;
        this.lastSparkTs = 0;
      }
    };
    this.sparkRaf = window.requestAnimationFrame(tick);
  }

  private clearSparks(): void {
    if (this.sparkRaf !== null) {
      window.cancelAnimationFrame(this.sparkRaf);
      this.sparkRaf = null;
    }
    this.lastSparkTs = 0;
    this.sparks = [];
  }

  private moveActive(
    direction: 'up' | 'down' | 'left' | 'right',
    shouldScroll = true,
  ): void {
    const sections = this.sections;
    if (!sections.length) {
      return;
    }

    let offset = 0;
    let sectionIdx = 0;
    let localIdx = 0;
    for (let i = 0; i < sections.length; i += 1) {
      const len = sections[i].items.length;
      if (this.activeIndex < offset + len) {
        sectionIdx = i;
        localIdx = this.activeIndex - offset;
        break;
      }
      offset += len;
    }

    const section = sections[sectionIdx];
    const count = section.items.length;
    if (!count) {
      return;
    }
    const isGrid = this.isGridSection(section.key);
    const cols = isGrid ? this.getGridColumnCount(section.key) : 1;

    // ← → : reading order (±1). End of row continues to the next/previous row;
    // end of section continues to the next/previous section.
    if (direction === 'left' || direction === 'right') {
      const delta = direction === 'right' ? 1 : -1;
      const next = localIdx + delta;
      if (next >= 0 && next < count) {
        this.activeIndex = offset + next;
        if (shouldScroll) {
          this.scrollActiveIntoView();
        }
        return;
      }
      if (direction === 'right') {
        this.moveToSection(sectionIdx + 1, 0, 'start', shouldScroll);
      } else {
        this.moveToSection(sectionIdx - 1, 0, 'end', shouldScroll);
      }
      return;
    }

    // ↑ ↓ : move by row (grid) or by item (list). Past the edge continues
    // to the next/previous section, keeping column when possible.
    if (isGrid && cols > 1) {
      const delta = direction === 'down' ? cols : -cols;
      const next = localIdx + delta;
      if (next >= 0 && next < count) {
        this.activeIndex = offset + next;
        if (shouldScroll) {
          this.scrollActiveIntoView();
        }
        return;
      }
      if (direction === 'down') {
        this.moveToSection(sectionIdx + 1, localIdx % cols, 'start', shouldScroll);
      } else {
        this.moveToSection(sectionIdx - 1, localIdx % cols, 'end', shouldScroll);
      }
      return;
    }

    if (direction === 'down') {
      if (localIdx + 1 < count) {
        this.activeIndex = offset + localIdx + 1;
        if (shouldScroll) {
          this.scrollActiveIntoView();
        }
        return;
      }
      this.moveToSection(sectionIdx + 1, 0, 'start', shouldScroll);
      return;
    }

    if (localIdx > 0) {
      this.activeIndex = offset + localIdx - 1;
      if (shouldScroll) {
        this.scrollActiveIntoView();
      }
      return;
    }
    this.moveToSection(sectionIdx - 1, 0, 'end', shouldScroll);
  }

  private moveToSection(
    sectionIdx: number,
    preferredCol: number,
    edge: 'start' | 'end',
    shouldScroll = true,
  ): void {
    const sections = this.sections;
    if (!sections.length) {
      return;
    }

    // Wrap past the first/last section to the opposite end.
    if (sectionIdx < 0) {
      sectionIdx = sections.length - 1;
      edge = 'end';
    } else if (sectionIdx >= sections.length) {
      sectionIdx = 0;
      edge = 'start';
    }

    const section = sections[sectionIdx];
    if (!section.items.length) {
      return;
    }

    let offset = 0;
    for (let i = 0; i < sectionIdx; i += 1) {
      offset += sections[i].items.length;
    }

    if (!this.isGridSection(section.key)) {
      this.activeIndex = edge === 'start' ? offset : offset + section.items.length - 1;
      if (shouldScroll) {
        this.scrollActiveIntoView();
      }
      return;
    }

    const cols = this.getGridColumnCount(section.key);
    const count = section.items.length;
    if (edge === 'start') {
      this.activeIndex = offset + Math.min(Math.max(preferredCol, 0), count - 1);
    } else {
      const lastRowStart = Math.floor((count - 1) / cols) * cols;
      this.activeIndex = offset + Math.min(lastRowStart + Math.max(preferredCol, 0), count - 1);
    }
    if (shouldScroll) {
      this.scrollActiveIntoView();
    }
  }

  private getGridColumnCount(sectionKey: string): number {
    const root = this.$refs.results as HTMLElement | undefined;
    if (!root) {
      return 1;
    }
    const grid = root.querySelector(`[data-section="${sectionKey}"] .global-palette__grid`) as HTMLElement | null;
    if (!grid) {
      return 1;
    }
    const template = window.getComputedStyle(grid).gridTemplateColumns || '';
    const cols = template.split(/\s+/).filter((part) => !!part && part !== 'none');
    return Math.max(1, cols.length);
  }
}
